import {Component, computed, input, signal} from '@angular/core';
import {Button} from 'primeng/button';
import {TranslocoPipe} from '@jsverse/transloco';
import {HrToolAiCapabilityView} from '@frontend/shared-api';
import {AiCapabilityLabelComponent} from '../../../../shared/components/ai-capability-label/ai-capability-label.component';
import {AiCapabilityDotsComponent} from '../../../../shared/components/ai-capability-dots/ai-capability-dots.component';
import {AiCapabilityLevelLabelComponent} from '../../../../shared/components/ai-capability-level-label/ai-capability-level-label.component';
import {
  AiCapabilityComparisonCell,
  AiCapabilityComparisonRow,
  buildAiCapabilityComparison,
  previewUseCases,
} from '../../../../shared/utils/hr-tool-ai-capability.utils';
import {
  AiCapabilityComparisonCellView,
  AiCapabilityComparisonRowView,
} from './hr-tool-ai-capability-comparison.types';

const PREVIEWED_USE_CASES = 2;

@Component({
  selector: 'app-hr-tool-ai-capability-comparison',
  imports: [
    Button,
    TranslocoPipe,
    AiCapabilityLabelComponent,
    AiCapabilityDotsComponent,
    AiCapabilityLevelLabelComponent,
  ],
  templateUrl: './hr-tool-ai-capability-comparison.component.html',
})
export class HrToolAiCapabilityComparisonComponent {
  readonly tools = input<readonly {id: string; aiCapabilities?: HrToolAiCapabilityView[]}[]>([]);

  private readonly expandedCells = signal<ReadonlySet<string>>(new Set());

  protected readonly rows = computed<AiCapabilityComparisonRowView[]>(() => {
    const expanded = this.expandedCells();
    return buildAiCapabilityComparison(this.tools()).map((row) => this.toRowView(row, expanded));
  });
  protected readonly hasRows = computed(() => this.rows().length > 0);
  protected readonly columnCount = computed(() => this.tools().length);

  protected toggleUseCases(cellId: string): void {
    this.expandedCells.update((current) => {
      const next = new Set(current);
      if (!next.delete(cellId)) {
        next.add(cellId);
      }
      return next;
    });
  }

  private toRowView(
    row: AiCapabilityComparisonRow,
    expanded: ReadonlySet<string>,
  ): AiCapabilityComparisonRowView {
    return {
      capability: row.capability,
      testId: `comparison-ai-capability-${row.capability}`,
      cells: row.cells.map((cell) => this.toCellView(row, cell, expanded)),
    };
  }

  private toCellView(
    row: AiCapabilityComparisonRow,
    cell: AiCapabilityComparisonCell,
    expanded: ReadonlySet<string>,
  ): AiCapabilityComparisonCellView {
    const testId = `comparison-ai-capability-${row.capability}-${cell.toolId}`;
    const isExpanded = expanded.has(testId);
    const preview = previewUseCases(cell.useCases, PREVIEWED_USE_CASES);
    return {
      toolId: cell.toolId,
      level: cell.level,
      shownUseCasesText: (isExpanded ? cell.useCases : preview.visible).join(', '),
      hasUseCases: cell.useCases.length > 0,
      canToggle: preview.hiddenCount > 0,
      expanded: isExpanded,
      moreLabel: `+${preview.hiddenCount}`,
      testId,
      toggleTestId: `${testId}-toggle`,
    };
  }
}
